'use client';

import React from 'react';
import Link from 'next/link';
import { signIn } from 'next-auth/react';
import z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { AppleIcon, FacebookIcon, GoogleIcon } from '~/components/svgIcons';
import { useAppRouter } from '~/hooks';
import { ERROR_CODES } from '~/lib/errors';
import { logger } from '~/lib/utils';
import { AuthContainer } from '../AuthContainer';
import { Loader, Lock, Mail } from 'lucide-react';

// Zod schema for signin form validation
const signInSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  rememberMe: z.boolean().optional(),
});

type SignInFormData = z.infer<typeof signInSchema>;

interface SignInFormProps {
  callbackUrl: string;
}

export function SignInForm({ callbackUrl }: SignInFormProps) {
  const router = useAppRouter();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setErrorMessage] = React.useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({
    values: {
      email: '',
      password: '',
      rememberMe: false,
    },
    resolver: zodResolver(signInSchema),
    mode: 'onBlur',
  });

  const handleSignIn = async (values: SignInFormData) => {
    try {
      setIsLoading(true);

      const result = await signIn('credentials', {
        email: values.email,
        password: values.password,
        rememberMe: values.rememberMe,
        redirect: false,
      });

      if (result.error) {
        switch (result.code) {
          case ERROR_CODES.INVALID_CREDENTIALS:
            setError('password', { message: 'Invalid email or password' });
            toast.error('Invalid email or password');
            break;
          case ERROR_CODES.EMAIL_NOT_VERIFIED:
            setError('email', {
              message: 'Please verify your email before signing in',
            });
            toast.error('Please verify your email before signing in');
            break;
          case ERROR_CODES.MISSING_CREDENTIALS:
            setErrorMessage('Please enter your email and password');
            break;
          case ERROR_CODES.ACCOUNT_SUSPENDED:
            setErrorMessage(
              'Your account has been suspended. Contact support.',
            );
            break;
          case ERROR_CODES.AN_UNEXPECTED_ERROR_OCCURRED:
            setErrorMessage('An unexpected error occurred. Contact support.');
            break;
          default:
            setErrorMessage('An unexpected error occurred');
            break;
        }
      } else {
        toast.success('Signed in successfully!');
        router.push(callbackUrl);
      }
    } catch (error) {
      setErrorMessage('An unexpected error occurred');
      logger.error('An unexpected error occurred during sign in.', { error });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);

      const result = await signIn('google', {
        redirect: false,
        callbackUrl: callbackUrl,
      });

      if (result?.error) {
        toast.error(result.error || 'Failed to sign in with Google');
        setIsLoading(false);
        return;
      }

      if (result?.ok && result?.url) {
        router.push(result.url);
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
      setIsLoading(false);
      logger.error('An unexpected error occurred during Google sign in.', {
        error,
      });
    }
  };

  return (
    <>
      <AuthContainer
        title="Welcome Back"
        description="Sign in to your account to continue"
      >
        {error && (
          <span className="text-red-500 block mt-1 text-center">{error}</span>
        )}

        <form onSubmit={handleSubmit(handleSignIn)} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-foreground mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground-muted" />
              <input
                id="email"
                type="email"
                placeholder="you@example.com"
                className="w-full pl-10 pr-4 py-3 border border-input bg-input-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                {...register('email')}
              />
              {errors.email && (
                <span className="text-red-500">{errors.email.message}</span>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-foreground mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground-muted" />
              <input
                id="password"
                type="password"
                placeholder="Enter your password"
                className="w-full pl-10 pr-4 py-3 border border-input bg-input-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                {...register('password')}
              />
              {errors.password && (
                <span className="text-red-500">{errors.password.message}</span>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                className="w-4 h-4 text-primary border-input rounded focus:ring-primary"
                {...register('rememberMe')}
              />
              <span className="text-foreground-secondary">Remember me</span>
            </label>
            <Link
              href="/auth/forgot-password"
              className="text-primary hover:opacity-80"
            >
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity shadow-lg flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <Loader className="w-5 h-5 animate-spin" />
            ) : (
              <Lock className="w-5 h-5" />
            )}
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {/* Social Sign In Options */}
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-card-border"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-card px-4 text-foreground-secondary">
                Or continue with
              </span>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3">
            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="flex items-center justify-center px-4 py-3 border border-card-border rounded-lg bg-secondary hover:bg-background-secondary transition-colors"
            >
              {isLoading ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <GoogleIcon />
              )}
            </button>

            <button
              type="button"
              disabled={true}
              className="disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center px-4 py-3 border border-card-border rounded-lg bg-secondary hover:bg-background-secondary transition-colors"
            >
              <AppleIcon />
            </button>

            <button
              type="button"
              disabled={true}
              className="disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center px-4 py-3 border border-card-border rounded-lg bg-secondary hover:bg-background-secondary transition-colors"
            >
              <FacebookIcon />
            </button>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-foreground-secondary">
            Don&apos;t have an account?{' '}
            <Link href="/auth/signup" className="text-primary hover:opacity-80">
              Sign up for free
            </Link>
          </p>
        </div>
      </AuthContainer>

      <div className="mt-6 text-center text-foreground-secondary">
        <p>
          By signing in, you agree to our{' '}
          <Link href="/terms" className="text-primary hover:opacity-80">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link href="/privacy" className="text-primary hover:opacity-80">
            Privacy Policy
          </Link>
        </p>
      </div>
    </>
  );
}
